import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { data, rotulo } from '../formato';
import { Aviso, Carregando, ErroDaApi, LinkProtegido, Selo, SeloDoEnsaio, useEstreito } from '../ui';
import { Seletor, SeletorDeCliente, SeletorDeOs, type OpcaoDeFiltro } from '../Filtros';
import { CamposDaFicha } from '../TabelaComFicha';
import { NOME_DA_FICHA, acaoDe, telasDe } from '../acaoDoEnsaio';
import { Lixeira } from '../Lixeira';
import { NomeDaAmostra, daApi, nomeDaAmostra } from '../NomeDaAmostra';
import {
  AlternarDaBarra, BarraDeLista, BuscaDaBarra, FiltrosAtivos, Monograma,
  ResumoQueFiltra, RodapeDaLista, usePaginacao, type FiltroAtivo, type ItemDoResumo,
} from '../BarraDeLista';
import {
  Periodo, diaDaSemana, diaMes, diasEntre, hojeIso, periodoDoAtalho,
  textoDoPeriodo, type ValorDoPeriodo,
} from '../Periodo';

/**
 * A fila do laboratório — em duas colunas, na ordem do cronograma (onda 270).
 *
 * «Principalmente a fila do laboratório, que não está clara e bem definida.
 *  Não está intuitiva para os laboratoristas. Separe em 2 listas lado a
 *  lado (avalie se fica bom), separando em ensaios especiais e ensaios
 *  triviais. A ordenação da fila do laboratório deve ser de acordo com o
 *  cronograma, tendo os ensaios que estão com data de início antes,
 *  apresentados no início das listas. […] Na fila do laboratório deixe
 *  destacado o ensaio e o cliente.» — Humberto, 03/10/2026. Ele escolheu a
 *  proposta A (duas colunas) no canvas «Listas do LabManager · Carbon
 *  Testesolo», em 04/10.
 *
 * ── Por que duas colunas, e não um filtro de classe ──────────────────
 *
 * A 0040 (23/08) fez de trivial × especial um FILTRO, para o menu parar de
 * crescer. O filtro respondia «o que tem de especial parado?», mas obrigava
 * o laboratorista a escolher UMA das duas perguntas — e a bancada trabalha
 * com as duas ao mesmo tempo: a umidade que sai hoje e o adensamento que
 * está na prensa desde o dia 22. Lado a lado, cada coluna tem o seu ritmo
 * (o trivial sai no dia, o especial ocupa máquina por dias) e a pessoa vê
 * o dia inteiro de uma vez. No celular as duas viram abas.
 *
 * ── No celular (onda 309, spec 06 §6, item 5) ────────────────────────
 *
 * O cartão inteiro é o alvo e leva à ficha de lançamento — o destino do
 * «Lançar», «Corrigir» ou «Verificar» de sempre (D3). O LA, de duas
 * fichas, tem «Lançar LL» e «Lançar LP» de 48 px; o ensaio sem ação (a
 * amostra que não chegou) continua abrindo a ficha, que diz o motivo.
 * «Abrir no cronograma» e as frases da ordem saem (o Cronograma está no
 * «Mais», e os grupos de dia já dizem a ordem), e cada coluna vem de 30 em
 * 30: o primeiro ensaio aparece sem rolar.
 *
 * ── A ordem é a do cronograma ────────────────────────────────────────
 *
 * Os ensaios ficam agrupados pelo DIA de início programado, do mais cedo
 * para o mais tarde: o que começou antes e ainda está aberto vem em cima,
 * depois «Hoje», depois os próximos. Até a 269 a fila abria pelo ensaio
 * mais parado na etapa — a pergunta do gestor. Ela continua na API
 * (`ordem=parado`) e na tela de Verificação e aprovação, que ordena por
 * «parada há».
 *
 * ── O que continua, da fila antiga ───────────────────────────────────
 *
 * Os três avisos que faziam dela uma fila e não um relatório: de quem o
 * ensaio está esperando, há quantos dias está parado na etapa, e se existe
 * alguém que possa verificar (a segregação de funções do item 15). E o
 * link «lançar» só nos métodos cujos cálculos a Testesolo conferiu.
 */

interface Ensaio {
  id: string;
  status: string;
  esperando: string | null;
  aberto: boolean;
  dias_na_etapa: number | null;
  verificadores_possiveis: number;
  /** Este método EXIGE competência cadastrada? (0080) */
  metodo_exige_competencia: boolean;
  metodo_codigo: string;
  metodo_nome: string;
  /** Onde se lança este método. Nulo = ainda não há ficha (0076). */
  telas_lancamento: string[] | null;
  /** Por que este ensaio ainda não pode começar — ou nulo, se pode (0078). */
  impedimento_amostra: string | null;
  /** Verdadeiro quando o impedimento não passa com o tempo. */
  impedimento_definitivo: boolean | null;
  metodo_categoria: string | null;
  /** Trivial × especial (0040) — a coluna em que o ensaio mora. */
  metodo_classe: string | null;
  amostra_id: string | null;
  amostra_codigo: string | null;
  amostra_identificacao: string | null;
  ponto_investigacao: string | null;
  os_numero: string | null;
  cliente: string | null;
  obra_nome: string | null;
  executado_por_nome: string | null;
  verificado_por_nome: string | null;
  data_execucao: string | null;
  data_prevista: string | null;
  atrasado: boolean;
  dias_de_atraso: number | null;
  motivo_devolucao: string | null;
  /* ── Da programação (`vw_programacao_ensaio`, onda 270) ── */
  inicio_previsto: string | null;
  fim_previsto: string | null;
  duracao_dias: number | string | null;
  equipamento_codigo: string | null;
  equipamento_nome: string | null;
  frente_nome: string | null;
  inicio_real: string | null;
}

/*
 * Onde se lança cada ensaio (`telasDe`), como cada ficha se chama
 * (`NOME_DA_FICHA`) e o próximo passo da bancada (`acaoDe`) moram em
 * `acaoDoEnsaio.ts` desde a onda 373: a tela da amostra (a do QR) oferece o
 * mesmo botão, e a regra é uma só. A história da tabela de prefixos e do LA
 * de duas fichas foi junto.
 */

// 267: o tom da situação do ensaio mora no selo único (`SeloDoEnsaio`, ui.tsx).

/* A escada inteira das situações, na ordem do §12. */
const SITUACOES: OpcaoDeFiltro[] = [
  'programado', 'em_execucao', 'dados_completos', 'em_processamento',
  'aguardando_verificacao', 'devolvido_para_correcao', 'verificado',
  'aprovado', 'cancelado', 'substituido',
].map((s) => ({ valor: s, texto: rotulo(s) }));

/**
 * As classes de método, vindas do catálogo (`classe_metodo`, 0040).
 *
 * As colunas da tela SÃO as classes do catálogo, na ordem dele (trivial
 * primeiro, que é o que sai no dia) — a tela não guarda a própria cópia da
 * lista, pela mesma razão que a 0040 deu: o dia em que uma classe nova
 * entrar no catálogo, ela vira coluna sozinha.
 */
interface Classe {
  codigo: string;
  nome: string;
  descricao: string;
  quantos: number;
}

/** O que o padrão escreve quando o catálogo não respondeu (sem permissão). */
const CLASSES_PADRAO: Classe[] = [
  { codigo: 'trivial', nome: 'Triviais', descricao: 'Saem no mesmo dia ou no dia seguinte.', quantos: 1 },
  { codigo: 'especial', nome: 'Especiais', descricao: 'Ocupam máquina por dias.', quantos: 1 },
];

/** O recorte do resumo que filtra — o que cada número quer dizer. */
const RECORTES: { chave: string; rotulo: string; aux: string; tom: ItemDoResumo['tom']; f(e: Ensaio, hoje: string): boolean }[] = [
  { chave: 'atrasados', rotulo: 'Atrasados', aux: 'o início programado já passou', tom: 'critico', f: (e) => e.aberto && e.atrasado },
  { chave: 'hoje', rotulo: 'Começam hoje', aux: 'início programado para hoje', tom: 'marcador', f: (e, h) => e.aberto && e.inicio_previsto?.slice(0, 10) === h },
  { chave: 'execucao', rotulo: 'Em execução', aux: 'na bancada ou na máquina', tom: 'andamento', f: (e) => e.status === 'em_execucao' },
  { chave: 'verificacao', rotulo: 'Aguardam verificação', aux: 'esperando outra pessoa', tom: 'atencao', f: (e) => e.status === 'aguardando_verificacao' },
  { chave: 'devolvidos', rotulo: 'Devolvidos', aux: 'voltaram para correção', tom: 'critico', f: (e) => e.status === 'devolvido_para_correcao' },
];

const SEM_DATA = 'sem-data';

/** O catálogo nomeia a classe no singular («Trivial»); a coluna é o plural. */
const PLURAL: Record<string, string> = { trivial: 'Triviais', especial: 'Especiais' };

/** O período que a fila abre: os próximos 7 dias e o que ficou para trás. */
const periodoInicial = () => periodoDoAtalho('proximos7', { pendentes: true });

export function Fila() {
  const [params, setParams] = useSearchParams();
  const [linhas, setLinhas] = useState<Ensaio[] | null>(null);
  const [classes, setClasses] = useState<Classe[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [aberta, setAberta] = useState<string | null>(null);
  const estreito = useEstreito(640);
  const hoje = hojeIso();

  /*
   * Os filtros moram no endereço (padrão de lista, 04/10): a fila filtrada
   * se manda a um colega, e o F5 não desfaz o que a pessoa escolheu.
   */
  const busca = params.get('q') ?? '';
  const status = params.get('sit');
  const cliente = params.get('cli');
  const os = params.get('os');
  const recorte = params.get('rec');
  const soAbertos = params.get('todos') !== '1';
  const aba = params.get('aba') ?? 'trivial';
  const periodo: ValorDoPeriodo = params.has('de') || params.has('ate') || params.has('pend')
    ? {
      de: params.get('de') ?? '', ate: params.get('ate') ?? '',
      atalho: params.get('at'), pendentes: params.get('pend') !== '0',
    }
    : periodoInicial();

  const mudar = useCallback((mudancas: Record<string, string | null>) => {
    setParams((p) => {
      const q = new URLSearchParams(p);
      for (const [k, v] of Object.entries(mudancas)) {
        if (v === null || v === '') q.delete(k); else q.set(k, v);
      }
      return q;
    }, { replace: true });
  }, [setParams]);

  const mudarPeriodo = (v: ValorDoPeriodo) => mudar({
    de: v.de || '', ate: v.ate || '', at: v.atalho ?? null,
    pend: v.pendentes ? '1' : '0',
  });

  /*
   * O catálogo carrega uma vez. Se a pessoa não puder ler cadastros a
   * chamada devolve 403 — e as colunas saem com os nomes padrão, em vez
   * de a tela inteira quebrar por causa de um rótulo.
   */
  useEffect(() => {
    api<Classe[]>('/normas/classes')
      .then(setClasses)
      .catch(() => setClasses(CLASSES_PADRAO));
  }, []);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      if (busca) q.set('busca', busca);
      if (status) q.set('status', status);
      if (cliente) q.set('cliente', cliente);
      if (os) q.set('os', os);
      if (soAbertos) q.set('abertos', 'true');
      if (periodo.de) q.set('de', periodo.de);
      if (periodo.ate) q.set('ate', periodo.ate);
      if (periodo.pendentes) q.set('pendentes', 'true');
      setLinhas(await api<Ensaio[]>(`/ensaios?${q}`));
    } catch (e) { setErro(e); }
  }, [busca, status, cliente, os, soAbertos, periodo.de, periodo.ate, periodo.pendentes]);

  useEffect(() => {
    const t = setTimeout(() => { void carregar(); }, 250);
    return () => clearTimeout(t);
  }, [carregar]);

  const rec = RECORTES.find((r) => r.chave === recorte) ?? null;
  const visiveis = useMemo(
    () => (linhas ?? []).filter((e) => !rec || rec.f(e, hoje)),
    [linhas, rec, hoje]);

  /* A ficha fecha quando o filtro tira o ensaio aberto da lista. */
  const ensaioAberto = aberta ? visiveis.find((e) => e.id === aberta) ?? null : null;
  useEffect(() => { if (aberta && !ensaioAberto) setAberta(null); }, [aberta, ensaioAberto]);
  /* Esc fecha a ficha de onde estiver o foco — o calendário aberto segura o
     Esc para si (`stopPropagation` no Periodo), e fecha primeiro. */
  useEffect(() => {
    if (!ensaioAberto) return;
    const tecla = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setAberta(null); };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [ensaioAberto]);

  /**
   * Ensaio que a competência realmente trava: esperando verificação, o
   * método EXIGE autorização, e não há ninguém autorizado além de quem
   * executou (0080).
   */
  const travados = (linhas ?? []).filter(
    (e) => e.status === 'aguardando_verificacao'
        && e.metodo_exige_competencia
        && e.verificadores_possiveis === 0);

  const catalogo = classes.length ? classes : CLASSES_PADRAO;
  const codigos = new Set(catalogo.map((c) => c.codigo));
  const colunas = catalogo
    .filter((c) => c.codigo === 'trivial' || c.codigo === 'especial'
      || visiveis.some((e) => e.metodo_classe === c.codigo))
    .map((c) => ({
      ...c, nome: PLURAL[c.codigo] ?? c.nome,
      ensaios: visiveis.filter((e) => e.metodo_classe === c.codigo),
    }));
  const semClasse = visiveis.filter((e) => !e.metodo_classe || !codigos.has(e.metodo_classe));
  const abaValida = colunas.some((c) => c.codigo === aba) ? aba : colunas[0]?.codigo;

  const resumo: ItemDoResumo[] = RECORTES.map((r) => ({
    chave: r.chave, rotulo: r.rotulo, aux: r.aux, tom: r.tom,
    numero: (linhas ?? []).filter((e) => r.f(e, hoje)).length,
  }));

  const ativos: FiltroAtivo[] = [];
  if (rec) ativos.push({ chave: 'rec', texto: rec.rotulo, tirar: () => mudar({ rec: null }) });
  if (status) ativos.push({ chave: 'sit', texto: `Situação: ${rotulo(status)}`, tirar: () => mudar({ sit: null }) });
  if (busca) ativos.push({ chave: 'q', texto: `Busca: ${busca}`, tirar: () => mudar({ q: null }) });
  if (cliente) ativos.push({ chave: 'cli', texto: 'Cliente escolhido', tirar: () => mudar({ cli: null, os: null }) });
  if (os) ativos.push({ chave: 'os', texto: 'OS escolhida', tirar: () => mudar({ os: null }) });
  /* O «Limpar filtros» da faixa: tudo. */
  const limparFiltros = () => mudar({ rec: null, sit: null, q: null, cli: null, os: null });
  /* O número e o «Limpar» da folha do celular (309, Ruling R13): só o que
     mora na folha — a situação, o cliente, a OS, o Período e «Só em
     aberto». A busca e a pílula estão à vista e ficam. O Período conta
     quando sai do de sempre (os próximos 7 dias e o que ficou para trás);
     «Só em aberto», quando a pessoa pede os concluídos. */
  const inicial = periodoInicial();
  const periodoMudou = periodo.de !== inicial.de || periodo.ate !== inicial.ate
    || periodo.pendentes !== inicial.pendentes;
  const naFolha = ativos.filter((f) => ['sit', 'cli', 'os'].includes(f.chave)).length
    + (periodoMudou ? 1 : 0) + (!soAbertos ? 1 : 0);
  const limparDaFolha = () => mudar({
    sit: null, cli: null, os: null, de: null, ate: null, at: null, pend: null, todos: null,
  });
  /* A pergunta que a fila responde: quando muda, cada coluna do celular
     recomeça em 30, como as outras listas (309). A aba não entra — cada
     coluna guarda o próprio «Mostrar mais». */
  const recomeco = [busca, status, cliente, os, recorte, soAbertos,
    periodo.de, periodo.ate, periodo.pendentes].join('|');

  return (
    <div className="fila">
      {/* No celular a cabeça é só o título: o trabalho começa na primeira
          tela (RN-251 d), o Cronograma está no «Mais» e os grupos de dia já
          dizem a ordem. */}
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Fila do laboratório</h1>
          {estreito ? null : <p className="sub">Na ordem do cronograma: o ensaio que começa antes aparece primeiro.</p>}
        </div>
        {estreito ? null : <Link className="botao secundario" to="/cronograma">Abrir no cronograma</Link>}
      </div>

      <ResumoQueFiltra rotulo="Resumo da fila" itens={resumo} ativo={recorte}
        aoEscolher={(k) => mudar({ rec: k })} />

      {travados.length > 0 && (
        <Aviso tipo="atencao" titulo="Verificação sem verificador possível">
          {travados.length === 1 ? 'Um ensaio está' : `${travados.length} ensaios estão`}{' '}
          aguardando verificação e <strong>não há ninguém habilitado</strong> que
          possa verificá-los — quem executou não pode verificar o próprio
          trabalho. Autorize outra pessoa no método, em Competências, ou o
          ensaio fica parado indefinidamente.
        </Aviso>
      )}

      <BarraDeLista rotulo="Filtros da fila" ativos={naFolha}
        aoLimpar={limparDaFolha} total={linhas ? visiveis.length : undefined}>
        <BuscaDaBarra valor={busca} aoMudar={(v) => mudar({ q: v })}
          placeholder="Ensaio, cliente, OS ou amostra" rotulo="Buscar ensaio" />
        <Periodo valor={periodo} aoMudar={mudarPeriodo} rotulo="Período"
          atalhos={['hoje', 'proximos7', 'semana', 'mes', 'tudo']}
          rotuloPendentes="Incluir o que ficou para trás: atrasados, devolvidos, aguardando verificação e o que ainda não tem data no cronograma" />
        <SeletorDeCliente naBarra valor={cliente}
          aoMudar={(c) => mudar({ cli: c, os: null })} />
        <SeletorDeOs naBarra valor={os} cliente={cliente} aoMudar={(v) => mudar({ os: v })} />
        <Seletor naBarra rotulo="Situação" valor={status} opcoes={SITUACOES}
          aoMudar={(v) => mudar({ sit: v })} todos={soAbertos ? 'Tudo em aberto' : 'Todas'} />
        <AlternarDaBarra ligado={soAbertos} aoMudar={(v) => mudar({ todos: v ? null : '1' })}>
          {soAbertos ? '✓ Só em aberto' : 'Com os concluídos'}
        </AlternarDaBarra>
      </BarraDeLista>

      <FiltrosAtivos itens={ativos} aoLimpar={limparFiltros}
        contagem={linhas ? `${visiveis.length} ${visiveis.length === 1 ? 'ensaio' : 'ensaios'} · ${textoDoPeriodo(periodo)}` : undefined} />

      {estreito && colunas.length > 1 && (
        <div className="fila-abas" role="tablist" aria-label="Classe do ensaio">
          {colunas.map((c) => (
            <button key={c.codigo} type="button" role="tab"
              aria-selected={abaValida === c.codigo}
              className={abaValida === c.codigo ? 'aqui' : ''}
              onClick={() => mudar({ aba: c.codigo })}>
              {c.nome} <span className="mono">{c.ensaios.length}</span>
            </button>
          ))}
        </div>
      )}

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : (
          <div className={`fila-area${ensaioAberto ? ' com-ficha' : ''}`}>
            <div className={`fila-colunas n${colunas.length}`}>
              {colunas.map((c) => (
                <ColunaDaFila key={c.codigo} classe={c} ensaios={c.ensaios} hoje={hoje}
                  estreito={estreito} recomeco={recomeco}
                  escondida={estreito && abaValida !== c.codigo}
                  aberta={aberta} aoAbrir={(id) => setAberta(aberta === id ? null : id)}
                  vazio={linhas.length === 0
                    ? 'Nenhum ensaio na fila neste período. Ensaios entram pela programação da ordem de serviço.'
                    : ativos.length
                      ? `Nenhum ensaio ${c.nome.toLowerCase()} com estes filtros.`
                      : `Nenhum ensaio ${c.nome.toLowerCase()} neste período.`} />
              ))}
              {semClasse.length > 0 && (
                <ColunaDaFila classe={{
                  codigo: 'sem-classe', nome: 'Sem classe',
                  descricao: 'O método não diz se é trivial ou especial — corrija no cadastro do método.',
                  quantos: semClasse.length,
                }} ensaios={semClasse} hoje={hoje} escondida={false} larga
                  estreito={estreito} recomeco={recomeco}
                  aberta={aberta} aoAbrir={(id) => setAberta(aberta === id ? null : id)} vazio="" />
              )}
            </div>

            {ensaioAberto && (
              <FichaDoEnsaio e={ensaioAberto} hoje={hoje}
                aoFechar={() => setAberta(null)} aoApagar={() => { void carregar(); }} />
            )}
          </div>
        )}

      {/*
        * O rodapé diz por que o link «Lançar» falta em algumas linhas —
        * texto que contradiz o botão ao lado ensina a não ler o rodapé
        * (23/08), e por isso ele fala do que a tela FAZ. No celular não há
        * link «Lançar» (o cartão é o alvo), e o rodapé diz o que o toque faz
        * (Ruling R29).
        */}
      <p className="rodape-regra">
        {estreito ? (
          <>
            O toque leva ao próximo passo — lançar, corrigir ou verificar. Sem
            passo possível, abre a ficha do ensaio, que diz o motivo.
          </>
        ) : (
          <>
            O link <strong>Lançar</strong> aparece só nos métodos cuja tela de
            lançamento já teve os cálculos conferidos contra as planilhas da
            Testesolo.
          </>
        )}{' '}
        A data de cada grupo é o início programado no cronograma;
        o período mostra o ensaio que está correndo em algum dia dele.
      </p>
    </div>
  );
}

/* ── Uma coluna: os ensaios de uma classe, agrupados por dia ──────────── */

function ColunaDaFila({ classe, ensaios, hoje, escondida, aberta, aoAbrir, vazio, larga, estreito, recomeco }: {
  classe: Classe; ensaios: Ensaio[]; hoje: string; escondida: boolean;
  aberta: string | null; aoAbrir(id: string): void; vazio: string; larga?: boolean;
  estreito: boolean; recomeco: string;
}) {
  /*
   * No celular a coluna vem de 30 em 30 (RN-251 d), com o «Mostrar mais» das
   * outras listas (o `usePaginacao` que acumula e o `RodapeDaLista`). O corte
   * atravessa os dias: são 30 ensaios, não 30 por dia. No desktop a coluna
   * mostra tudo, como sempre — uma página do tamanho da coluna: a bancada
   * vê o dia inteiro de uma vez.
   */
  const pag = usePaginacao(ensaios, Math.max(ensaios.length, 1), recomeco);
  const grupos = useMemo(() => {
    const g: { dia: string; itens: Ensaio[] }[] = [];
    for (const e of pag.visiveis) {
      const dia = e.inicio_previsto?.slice(0, 10) ?? SEM_DATA;
      const ultimo = g[g.length - 1];
      if (ultimo && ultimo.dia === dia) ultimo.itens.push(e);
      else g.push({ dia, itens: [e] });
    }
    return g;
  }, [pag.visiveis]);

  return (
    <section className={`fila-coluna classe-${classe.codigo}${larga ? ' larga' : ''}`}
      hidden={escondida} aria-label={`${classe.nome}: ${ensaios.length} ${ensaios.length === 1 ? 'ensaio' : 'ensaios'}`}>
      <header className="fila-coluna-cabeca">
        <h2>{classe.nome}</h2>
        <span className="n mono">{ensaios.length}</span>
        {estreito ? null : <span className="ordem">Ordem: início no cronograma ↑</span>}
        <span className="descricao">{classe.descricao}</span>
      </header>
      {ensaios.length === 0 && vazio && <p className="fila-vazio">{vazio}</p>}
      {grupos.map((g) => (
        <div key={g.dia} className="fila-dia">
          <CabecaDoDia dia={g.dia} hoje={hoje} />
          {g.itens.map((e) => (
            <ItemDaFila key={e.id} e={e} hoje={hoje} aberto={aberta === e.id}
              estreito={estreito} aoAbrir={() => aoAbrir(e.id)} />
          ))}
        </div>
      ))}
      {pag.acumula && <RodapeDaLista {...pag} />}
    </section>
  );
}

function CabecaDoDia({ dia, hoje }: { dia: string; hoje: string }) {
  if (dia === SEM_DATA) {
    return <div className="fila-dia-cabeca sem-data">Sem data no cronograma</div>;
  }
  const n = diasEntre(hoje, dia);
  const quando = n === 0 ? '' : n === -1 ? 'começou ontem' : n < 0 ? `começou há ${-n} dias`
    : n === 1 ? 'amanhã' : `em ${n} dias`;
  return (
    <div className={`fila-dia-cabeca${n === 0 ? ' hoje' : n < 0 ? ' passado' : ''}`}>
      {n === 0 ? 'Hoje · ' : ''}{diaDaSemana(dia)} {diaMes(dia)}
      {quando && <em>{quando}</em>}
    </div>
  );
}

/** «dia 9 de 10» para o especial que está correndo na máquina. */
function andamentoNaMaquina(e: Ensaio, hoje: string): { dia: number; de: number } | null {
  const dur = Number(e.duracao_dias);
  const comeco = e.inicio_real?.slice(0, 10) ?? e.inicio_previsto?.slice(0, 10);
  if (e.status !== 'em_execucao' || !comeco || !(dur > 1)) return null;
  const de = Math.ceil(dur);
  return { dia: Math.max(1, Math.min(diasEntre(comeco, hoje) + 1, de)), de };
}

function SelosDoEnsaio({ e }: { e: Ensaio }) {
  return (
    <>
      {e.atrasado && e.aberto && (
        <Selo tom="critico" titulo={e.data_prevista ? `previsto para ${data(e.data_prevista)}` : undefined}>
          Atrasado {e.dias_de_atraso} {e.dias_de_atraso === 1 ? 'dia' : 'dias'}
        </Selo>
      )}
      <SeloDoEnsaio status={e.status} />
    </>
  );
}

function ItemDaFila({ e, hoje, aberto, aoAbrir, estreito }: {
  e: Ensaio; hoje: string; aberto: boolean; aoAbrir(): void; estreito: boolean;
}) {
  const telas = telasDe(e);
  const acao = acaoDe(e);
  const naMaquina = andamentoNaMaquina(e, hoje);
  const dur = Number(e.duracao_dias);
  const maquina = e.equipamento_codigo ?? e.frente_nome;
  const semVerificador = e.status === 'aguardando_verificacao'
    && e.metodo_exige_competencia && e.verificadores_possiveis === 0;
  /* O LA na bancada: um ensaio, duas fichas, dois links (30/08). */
  const duasFichas = !!acao && telas.length > 1 && acao.texto === 'Lançar';
  const classe = `fila-item${aberto ? ' sel' : ''}${e.aberto ? '' : ' apagada'}`;

  /* O que a linha mostra — o mesmo no desktop e no celular; muda a casca. */
  const nomeDoEnsaio = (
    <span className="fila-ensaio">
      <strong>{e.metodo_nome}</strong>
      <span className="cod">{e.metodo_codigo}</span>
    </span>
  );
  const resto = (
    <>
      <span className="fila-cliente">
        <Monograma nome={e.cliente} />
        <b>{e.cliente ?? 'Sem cliente'}</b>
        {e.os_numero && <span className="mono">OS {e.os_numero}</span>}
      </span>
      {/* RN-248 (onda 303): o nome do cliente em destaque, o código AM logo
          depois. A OS não se repete aqui — já está na linha do cliente,
          no mesmo cartão. */}
      <span className="fila-amostra">
        {e.amostra_codigo && <NomeDaAmostra amostra={daApi(e)} emLinha />}
        {e.ponto_investigacao && <span>{e.amostra_codigo ? ' · ' : ''}{e.ponto_investigacao}</span>}
      </span>
      {maquina && (
        <span className="fila-maquina">
          <span>
            {maquina}
            {dur > 0 ? ` · ${String(dur).replace('.', ',')} ${dur === 1 ? 'dia' : 'dias'}` : ''}
            {e.fim_previsto && dur > 1 ? ` · termina ${diaMes(e.fim_previsto.slice(0, 10))}` : ''}
          </span>
          {naMaquina && (
            <>
              <span className="avanco" aria-hidden="true">
                <i style={{ width: `${Math.round((naMaquina.dia / naMaquina.de) * 100)}%` }} />
              </span>
              <span>dia {naMaquina.dia} de {naMaquina.de}</span>
            </>
          )}
        </span>
      )}
    </>
  );
  const temAviso = !!(e.esperando && e.aberto) || semVerificador || !!e.impedimento_amostra;
  const avisos = (
    <>
      {e.esperando && e.aberto && (
        <span className="sub">
          esperando {e.esperando}
          {e.dias_na_etapa !== null && e.dias_na_etapa >= 1 ? ` há ${e.dias_na_etapa} ${e.dias_na_etapa === 1 ? 'dia' : 'dias'}` : ''}
        </span>
      )}
      {semVerificador && <span className="sub aviso-linha">nenhum verificador habilitado</span>}
      {/*
        * Se a amostra não está lá, a linha diz antes do clique (0078): a
        * espera é aviso (o cliente ainda pode mandar), a impossibilidade
        * é crítica (aquele material não volta).
        */}
      {e.impedimento_amostra && (
        <span className={`sub ${e.impedimento_definitivo ? 'critico-linha' : 'aviso-linha'}`}>
          {e.impedimento_amostra}
        </span>
      )}
    </>
  );
  /*
   * O botão que abre a ficha (o desktop, e o ensaio sem ação no celular).
   * No celular ele não leva rótulo, como o link (R19): o cartão inteiro está
   * dentro dele, e um `aria-label` por cima encobriria os selos e o aviso de
   * impedimento — justo o que diz por que o toque só abre a ficha.
   */
  const abreAFicha = {
    type: 'button' as const, className: 'fila-item-abre', 'aria-expanded': aberto,
    'aria-controls': aberto ? 'fila-ficha' : undefined,
    'aria-label': estreito ? undefined
      : `${e.metodo_nome}${e.cliente ? ` — ${e.cliente}` : ''}${e.amostra_codigo ? ` · ${nomeDaAmostra(daApi(e))}` : ''} — ${aberto ? 'fechar' : 'abrir'} a ficha`,
    onClick: aoAbrir,
  };

  /*
   * No celular o cartão é o alvo (D3). Com um destino só («Lançar»,
   * «Corrigir» ou «Verificar»), o corpo inteiro é o link — sem aria-label:
   * o nome é o que o cartão mostra (Ruling R19) — e o «Lançar →» solto
   * some. O LA, de duas fichas, não tem para onde levar com um toque só: o
   * corpo é texto e as duas ações viram botões de 48 px embaixo. Sem ação
   * (a amostra que não chegou), o botão de sempre, que abre a ficha com o
   * motivo. Os selos ficam DENTRO do corpo, no canto de cima — tocar neles
   * também abre —, e os avisos embaixo, na largura do cartão.
   */
  if (estreito) {
    const destino = acao && !duasFichas ? acao.para : null;
    const semFicha = !acao && telas.length === 0 && e.aberto;
    const conteudo = (
      <>
        {nomeDoEnsaio}
        <span className="fila-item-lado"><span className="selos"><SelosDoEnsaio e={e} /></span></span>
        {resto}
        {(temAviso || semFicha) && (
          <span className="fila-item-avisos">
            {avisos}
            {semFicha && <span className="sub nada">ficha de lançamento ainda não implementada</span>}
          </span>
        )}
      </>
    );
    return (
      <article className={classe} data-ensaio={e.id}>
        {destino ? (
          <Link className="fila-item-abre fila-item-link" to={destino}>{conteudo}</Link>
        ) : duasFichas ? (
          <>
            <div className="fila-item-corpo">{conteudo}</div>
            {/* Uma ação primária por cartão (P-UI-10): a primeira ficha
                («Lançar LL») é o primário; a segunda, o secundário da casa,
                como o «Pesar» de cada cartão da Estufa. */}
            <div className="fila-acoes-duplas">
              {telas.map((t, i) => (
                <Link key={t} className={i === 0 ? 'botao' : 'botao secundario'}
                  to={`/ensaio/${e.id}/${t}`}>Lançar {NOME_DA_FICHA[t] ?? t}</Link>
              ))}
            </div>
          </>
        ) : (
          <button {...abreAFicha}>{conteudo}</button>
        )}
      </article>
    );
  }

  return (
    <article className={classe} data-ensaio={e.id}>
      <button {...abreAFicha}>
        {nomeDoEnsaio}
        {resto}
      </button>
      <div className="fila-item-lado">
        <span className="selos"><SelosDoEnsaio e={e} /></span>
        {avisos}
        {duasFichas ? (
          <span className="fila-acao">
            {telas.map((t, i) => (
              <React.Fragment key={t}>
                {i > 0 && <span className="sep" aria-hidden="true"> · </span>}
                <Link to={`/ensaio/${e.id}/${t}`}>Lançar {NOME_DA_FICHA[t] ?? t}</Link>
              </React.Fragment>
            ))}
          </span>
        ) : acao ? (
          <Link className="fila-acao" to={acao.para}>{acao.texto} →</Link>
        ) : telas.length === 0 && e.aberto ? (
          <span className="sub nada">ficha de lançamento ainda não implementada</span>
        ) : null}
      </div>
    </article>
  );
}

/* ── A ficha do ensaio, à direita (o padrão de 28/09) ──────────────────── */

function FichaDoEnsaio({ e, hoje, aoFechar, aoApagar }: {
  e: Ensaio; hoje: string; aoFechar(): void; aoApagar(): void;
}) {
  const telas = telasDe(e);
  const acao = acaoDe(e);
  const semVerificador = e.status === 'aguardando_verificacao'
    && e.metodo_exige_competencia && e.verificadores_possiveis === 0;
  const dur = Number(e.duracao_dias);
  const naMaquina = andamentoNaMaquina(e, hoje);
  const inicio = e.inicio_previsto?.slice(0, 10);
  const fim = e.fim_previsto?.slice(0, 10);

  return (
    <aside id="fila-ficha" className="tf-ficha fila-ficha" aria-label={`Ficha: ${e.metodo_nome}`}>
      <div className="tf-ficha-cabeca">
        <div className="tf-ficha-titulos">
          <span className="tf-ficha-rotulo">Ensaio{e.metodo_classe ? ` · ${e.metodo_classe}` : ''}</span>
          <div className="tf-ficha-titulo">{e.metodo_nome}</div>
          <div className="tf-ficha-sub">
            <span className="fila-cliente"><Monograma nome={e.cliente} /><b>{e.cliente ?? 'Sem cliente'}</b>
              {e.os_numero && <span className="mono">OS {e.os_numero}</span>}</span>
          </div>
        </div>
        <button type="button" className="tf-fechar" aria-label="Fechar a ficha"
          title="Fechar (Esc)" onClick={aoFechar} />
      </div>
      <div className="tf-ficha-corpo">
        <div className="tf-selos"><SelosDoEnsaio e={e} /></div>
        {e.impedimento_amostra && (
          <div className={`tf-aviso ${e.impedimento_definitivo ? 'critico' : 'atencao'}`}>
            {e.impedimento_amostra}
          </div>
        )}
        {semVerificador && (
          <div className="tf-aviso atencao">
            Nenhum verificador habilitado disponível — autorize outra pessoa
            no método, em Competências.
          </div>
        )}
        <CamposDaFicha itens={[
          ['Código', <span className="mono">{e.metodo_codigo}</span>],
          ['Obra', e.obra_nome],
          ['Amostra', e.amostra_codigo ? (
            <>
              {/* O link segue pelo código (rota e QR); o que se mostra é o nome do cliente. */}
              <Link to={`/amostra/${encodeURIComponent(e.amostra_codigo)}`}><NomeDaAmostra amostra={daApi(e)} /></Link>
              {e.ponto_investigacao && <span className="sub">{e.ponto_investigacao}</span>}
            </>
          ) : null],
          ['Início programado', inicio ? `${diaDaSemana(inicio)}, ${data(inicio)}` : <span className="nada">sem data no cronograma</span>],
          ['Duração', dur > 0 ? `${String(dur).replace('.', ',')} ${dur === 1 ? 'dia' : 'dias corridos'}` : null],
          ['Término previsto', fim && fim !== inicio ? `${diaDaSemana(fim)}, ${data(fim)}` : null],
          ['Máquina', e.equipamento_codigo
            ? `${e.equipamento_codigo}${e.equipamento_nome ? ` · ${e.equipamento_nome}` : ''}`
            : e.frente_nome ?? null],
          ['Na máquina', naMaquina ? `dia ${naMaquina.dia} de ${naMaquina.de}` : null],
          ['Executou', e.executado_por_nome ?? (e.status === 'programado' ? null : <span className="nada">sem executante</span>)],
          ['Verificou', e.verificado_por_nome],
          ['Executado em', e.data_execucao ? data(e.data_execucao) : null],
          ['Esperando', e.esperando
            ? `${e.esperando}${e.dias_na_etapa !== null && e.dias_na_etapa >= 1 ? ` há ${e.dias_na_etapa} dia(s)` : ''}`
            : null],
          ['Devolvido', e.motivo_devolucao ? `“${e.motivo_devolucao}”` : null],
        ]} />
      </div>
      <div className="tf-ficha-acoes">
        <Link className="botao secundario" to={`/ensaio/${e.id}/situacao`}>Situação</Link>
        {acao && acao.texto !== 'Verificar' ? (
          <Link className="botao" to={acao.para}>
            {telas.length > 1 && acao.texto === 'Lançar'
              ? `Lançar ${NOME_DA_FICHA[telas[0]] ?? telas[0]}` : `${acao.texto} dados`}
          </Link>
        ) : acao ? (
          <Link className="botao" to={acao.para}>Verificar</Link>
        ) : telas.length > 0 && e.status !== 'programado' ? (
          <LinkProtegido className="botao" caminho={`/ensaios/${e.id}/laudo.pdf`}
            nomeQueda={`laudo-${e.id.slice(0, 8)}.pdf`}>
            Laudo PDF
          </LinkProtegido>
        ) : null}
      </div>
      <div className="fila-ficha-lixeira">
        <Lixeira tipo="ensaio" id={e.id}
          rotulo={`${e.metodo_codigo}${e.amostra_codigo ? ` ${nomeDaAmostra(daApi(e))}` : ''}`}
          aoApagar={aoApagar} />
      </div>
    </aside>
  );
}
